/**
 * Reglas de la clave que el trabajador se crea a sí mismo.
 *
 * Los accesos del piloto se entregan todos con la MISMA clave (`treid2026`). Mientras
 * esa clave siga puesta, cualquiera que sepa el documento de una compañera puede entrar
 * como ella y marcarle la asistencia — y todo este módulo existe justamente para probar
 * quién estuvo dónde. Por eso el cambio es obligatorio la primera vez, y por eso la
 * validación vive aquí, pura y probada, en vez de dentro de la pantalla.
 */

/** Clave con la que se entregan los accesos nuevos. No es un secreto: la sabe todo el equipo. */
export const CLAVE_GENERICA = 'treid2026';

/** Mínimo que impone Supabase Auth. Pedir menos hace que el servidor rechace el cambio. */
export const LARGO_MINIMO = 6;

const MENSAJE_GENERICA = 'Esa es la clave que te dieron. Crea una distinta, que solo tú conozcas.';

export interface ResultadoClave {
  ok: boolean;
  error: string | null;
}

const malo = (error: string): ResultadoClave => ({ ok: false, error });

/**
 * Valida la clave nueva contra su confirmación. Devuelve UN solo error a la vez, el
 * primero que corresponde: en un celular, en la puerta de la tienda, una lista de tres
 * reproches no se lee.
 *
 * `claveGenerica` se recibe por parámetro (no se lee de la constante) para que la regla
 * siga sirviendo el día que la clave de reparto cambie, sin tocar esta función.
 */
export function validarClaveNueva(
  nueva: string,
  confirmacion: string,
  claveGenerica: string,
): ResultadoClave {
  if (nueva.length === 0) return malo('Escribe tu clave nueva.');

  // Un espacio al inicio o al final es invisible en un campo de clave: se crearía hoy y
  // mañana la persona no podría entrar sin que nadie pueda explicarle por qué.
  if (nueva !== nueva.trim()) {
    return malo('La clave no puede empezar ni terminar con un espacio.');
  }

  if (nueva.length < LARGO_MINIMO) {
    return malo(`La clave debe tener al menos ${LARGO_MINIMO} caracteres.`);
  }

  // Sin distinguir mayúsculas: "Treid2026" es la misma clave repartida, no una propia.
  if (nueva.toLowerCase() === claveGenerica.trim().toLowerCase()) {
    return malo(MENSAJE_GENERICA);
  }

  if (nueva !== confirmacion) {
    return malo('Las dos claves no son iguales. Escríbelas otra vez.');
  }

  return { ok: true, error: null };
}
